import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getServerPaymentMethods } from './billing';

interface ApiKeyRecord {
  id: string;
  user_id: string;
  key_hash: string;
  key_prefix: string;
  title: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export const generateApiKey = (): string => {
  const prefix = 'nvbr_live_sk_';
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const randomBytes = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${prefix}${randomBytes}`;
};

export const hashApiKey = async (apiKey: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(apiKey);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const createApiKey = async (
  userId: string,
  title: string = 'Untitled Key'
): Promise<{ apiKey: string; keyId: string }> => {
  const paymentMethods = await getServerPaymentMethods(userId);
  if (!paymentMethods || paymentMethods.length === 0) {
    throw new Error('A payment method is required to create API keys. Please add one in billing settings.');
  }

  const apiKey = generateApiKey();
  const hashedKey = await hashApiKey(apiKey);
  const now = new Date().toISOString();

  const ref = await addDoc(collection(db, 'api_keys'), {
    user_id: userId,
    key_hash: hashedKey,
    key_prefix: apiKey.substring(0, 20) + '...',
    title,
    created_at: now,
    last_used_at: null,
    is_active: true,
  });

  return { apiKey, keyId: ref.id };
};

export const getUserApiKeys = async (userId: string): Promise<ApiKeyRecord[]> => {
  const snapshot = await getDocs(
    query(collection(db, 'api_keys'), where('user_id', '==', userId), where('is_active', '==', true))
  );

  return snapshot.docs
    .map((item) => ({ id: item.id, ...(item.data() as Omit<ApiKeyRecord, 'id'>) }))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

export const updateApiKeyTitle = async (keyId: string, userId: string, title: string): Promise<boolean> => {
  const keyRef = doc(db, 'api_keys', keyId);
  const keyDoc = await getDoc(keyRef);
  if (!keyDoc.exists() || keyDoc.data().user_id !== userId) {
    throw new Error('API key not found');
  }

  await updateDoc(keyRef, { title });
  return true;
};

export const revokeApiKey = async (keyId: string, userId: string): Promise<boolean> => {
  const keyRef = doc(db, 'api_keys', keyId);
  const keyDoc = await getDoc(keyRef);
  if (!keyDoc.exists() || keyDoc.data().user_id !== userId) {
    throw new Error('API key not found');
  }

  await updateDoc(keyRef, { is_active: false });
  return true;
};

export const verifyApiKey = async (apiKey: string): Promise<{ userId: string; keyId: string } | null> => {
  const hashedKey = await hashApiKey(apiKey);
  const snapshot = await getDocs(
    query(collection(db, 'api_keys'), where('key_hash', '==', hashedKey), where('is_active', '==', true))
  );

  const first = snapshot.docs[0];
  if (!first) {
    return null;
  }

  await updateDoc(doc(db, 'api_keys', first.id), { last_used_at: new Date().toISOString() });
  return { userId: first.data().user_id, keyId: first.id };
};
